import { z } from "zod";
import { FEATURE_KEYS } from "../types/index.js";
// ── Condition leaves ────────────────────────────────────────────────────────
const FEATURE_KEY_VALUES = Object.values(FEATURE_KEYS); // non-empty by construction: a const object literal
const fieldCapturedConditionSchema = z.object({
    type: z.literal("field_captured"),
    field: z.string().min(1),
    // The condition only exists for a tenant whose plan grants this feature. A
    // script is global, so a field only one kind of tenant can ever capture
    // (`zona_entrega` for a shop that delivers) would otherwise hold every other
    // tenant in the phase until `max_turns`. Pruned by `pruneConditionGroup`.
    requires_feature: z.enum(FEATURE_KEY_VALUES).optional(),
});
const minTurnsInPhaseConditionSchema = z.object({
    type: z.literal("min_turns_in_phase"),
    value: z.number().int().min(1),
});
const minScoreConditionSchema = z.object({
    type: z.literal("min_score"),
    value: z.number().int(),
});
const phaseWithTurnsConditionSchema = z.object({
    type: z.literal("phase_with_turns"),
    phase: z.string().min(1),
    min_turns: z.number().int().min(1),
});
export const conditionLeafSchema = z.discriminatedUnion("type", [
    fieldCapturedConditionSchema,
    minTurnsInPhaseConditionSchema,
    minScoreConditionSchema,
    phaseWithTurnsConditionSchema,
]);
// ── Condition groups (combinators) ──────────────────────────────────────────
export const conditionGroupSchema = z.union([
    z.object({ all_of: z.array(conditionLeafSchema).min(1) }),
    z.object({ any_of: z.array(conditionLeafSchema).min(1) }),
]);
// ── Phase ───────────────────────────────────────────────────────────────────
const phaseSchema = z.object({
    id: z.string().min(1).max(64),
    order: z.number().int().min(1),
    goal: z.string().min(1),
    instructions: z.string().optional(),
    // Short keys describing the kinds of follow-up options the AI should offer
    // while in this phase. Surfaced as a dedicated section in the system prompt
    // so the model can pick phase-appropriate buttons instead of generic ones.
    followup_hints: z.array(z.string().min(1)).optional(),
    // Per-phase guidance on what content to emphasize, possibly conditioned on
    // the prospect's chosen program type. Rendered alongside `instructions`.
    content_focus: z.string().optional(),
    exit_conditions: conditionGroupSchema.optional(),
    max_turns: z.number().int().min(1).optional(),
});
// ── Field-capture rules ─────────────────────────────────────────────────────
const fieldCaptureStrategySchema = z.enum([
    "after_n_substantive_turns",
    "after_field_captured",
]);
const fieldCaptureRuleSchema = z.object({
    field: z.string().min(1),
    strategy: fieldCaptureStrategySchema,
    value: z.number().int().min(1).optional(),
    depends_on: z.string().optional(),
    phase_hint: z.string().optional(),
});
// ── Scoring ─────────────────────────────────────────────────────────────────
const scoringRuleSchema = z.object({
    range: z
        .tuple([z.number().int(), z.number().int()])
        .refine(([lo, hi]) => lo <= hi, "range[0] must be ≤ range[1]"),
    signal: z.string().min(1),
});
const scoringSchema = z.object({
    rules: z.array(scoringRuleSchema).min(1),
});
// ── Handoff ─────────────────────────────────────────────────────────────────
const handoffSchema = z.object({
    user_requested: z.object({
        always_trigger: z.boolean(),
    }),
    ai_initiated: z
        .object({
        conditions: conditionGroupSchema,
    })
        .optional(),
});
// ── Available tools ─────────────────────────────────────────────────────────
export const KNOWN_TOOL_NAMES = [
    "capture_contact",
    "capture_qualification",
    "capture_candidates",
    "trigger_whatsapp_handoff",
    "score_lead",
    "suggest_followup_options",
    "send_document",
    "get_visit_slots",
    "book_visit",
    "reschedule_visit",
    "cancel_visit",
    "add_to_cart",
    "remove_from_cart",
    "confirm_order",
    "set_delivery_address",
    "check_delivery_coverage",
    "add_quote_line",
    "update_quote_line",
    "remove_quote_line",
    "submit_quote_request",
];
const toolNameSchema = z.enum(KNOWN_TOOL_NAMES);
// ── Agent identity ──────────────────────────────────────────────────────────
const agentIdentitySchema = z.object({
    role: z.string().min(1),
    tone: z.string().optional(),
    language: z.string().optional(),
    greeting_template: z.string().optional(),
    /**
     * Who the security-rules fallback hands the prospect over to, article included
     * ("el equipo de admisiones", "un asesor"). Superseded by `vocabulary.team`;
     * omitted from both → the generic default in ai/prompt/vocabulary.ts.
     */
    handoff_team: z.string().optional(),
});
// ── Vocabulary and capability config ────────────────────────────────────────
/**
 * What this business calls the things the agent talks about.
 *
 * Deliberately NOT inside `agent_identity`, which describes who the agent *is*.
 * This describes what the business *sells*, which is the script's own subject
 * matter — and it is per-script for a safety reason: `sync-scripts` never
 * repoints a deployment, so a script edit stays inert until a human activates
 * it from super-admin. A per-deployment field would land in the cached prefix
 * on the next cache expiry, unreviewed.
 *
 * **Every value is a noun phrase the code never inflects.** Spanish agreement
 * is why: a bare noun substituted into "Estos {X}" or "no hay {X} abiertos"
 * produces "Estos maquinarias" and "no hay temporadas abiertos". Where the
 * surrounding sentence would have to agree, the code is written so it does not
 * have to. Values that take an article carry it ("el equipo de admisiones"),
 * following the precedent set by `agent_identity.handoff_team`.
 *
 * Omitted keys fall back to GENERIC defaults in the code — never to another
 * industry's words. A tenant that configures nothing reads prose that is bland
 * but correct.
 */
const vocabularySchema = z.object({
    /** What the client sells, singular and bare: "programa", "máquina", "botella". */
    offering: z.string().trim().min(1).optional(),
    /** Plural and bare: "programas", "máquinas". */
    offering_plural: z.string().trim().min(1).optional(),
    /** The catalog as a whole, with article: "la oferta académica", "el catálogo". */
    offering_collective: z.string().trim().min(1).optional(),
    /** The intake window a purchase belongs to, bare: "ciclo", "temporada". Only
     *  meaningful for tenants whose catalog uses cycles. */
    intake_period: z.string().trim().min(1).optional(),
    intake_period_plural: z.string().trim().min(1).optional(),
    /** Who a prospect is handed to, with article: "el equipo de admisiones".
     *  Supersedes `agent_identity.handoff_team`, which stays readable for rows
     *  written before this key existed. */
    team: z.string().trim().min(1).optional(),
    /** The physical site, with article: "el instituto", "la sucursal". */
    venue: z.string().trim().min(1).optional(),
    /** The business itself, with article: "la escuela", "la distribuidora". */
    client_noun: z.string().trim().min(1).optional(),
});
/**
 * Per-capability configuration.
 *
 * A capability owns generic mechanics (how to offer a slot, when not to re-open
 * the booking flow) and knows nothing about the domain. What it *calls* the
 * thing, and anything the tenant wants emphasized, comes from here.
 *
 * `extra_instructions` is the escape hatch that keeps the primary layers clean:
 * whatever the code stops saying — domain examples, emphasis, house rules — the
 * script says instead.
 */
const capabilityConfigSchema = z.object({
    visits: z
        .object({
        /** With article, singular: "la visita al campus", "la demo en planta". */
        event_noun: z.string().trim().min(1).optional(),
        /** Bare plural, for headings: "visitas al campus", "demos en planta". */
        event_noun_plural: z.string().trim().min(1).optional(),
        extra_instructions: z.string().trim().min(1).optional(),
        /**
         * Where the appointment happens. `in_person` (the default — a campus tour,
         * a plant demo): the confirmation closes with how to get there, and the
         * invitation carries the client's map. `online` (a software demo by video
         * call): it closes with the call link instead, which comes from
         * `clients.meeting_url`. Behaviour, not wording, but the lines it switches
         * are code-originated — the `orders.delivery_topic` precedent.
         */
        mode: z.enum(["in_person", "online"]).optional(),
    })
        .optional(),
    documents: z
        .object({
        /** Domain examples of what the documents typically are, for the prompt:
         *  "folleto, plan de estudios, temario". */
        examples: z.string().trim().min(1).optional(),
        extra_instructions: z.string().trim().min(1).optional(),
    })
        .optional(),
    location: z
        .object({
        extra_instructions: z.string().trim().min(1).optional(),
    })
        .optional(),
    /**
     * The people a lead enquires FOR — children, in a school. A shop selling to
     * the buyer in front of it has none, which is why this is a capability and not
     * a fact about every lead.
     */
    candidates: z
        .object({
        /** With article, singular: "el alumno", "el participante". */
        noun: z.string().trim().min(1).optional(),
        /** Bare plural, for headings: "los alumnos", "los participantes". */
        noun_plural: z.string().trim().min(1).optional(),
        extra_instructions: z.string().trim().min(1).optional(),
    })
        .optional(),
    /**
     * The cart and the order. A school takes no orders; a shop does.
     *
     * There is deliberately NO `payment_terms` key. How a tenant collects is a fact
     * about that tenant, and a script is global — a payment sentence here would be
     * a claim about every shop running this script. Payment methods live in the KB,
     * where per-tenant facts belong.
     */
    orders: z
        .object({
        /** With article, singular: "el pedido", "la orden". */
        order_noun: z.string().trim().min(1).optional(),
        /** With article, singular: "el carrito", "la lista". */
        cart_noun: z.string().trim().min(1).optional(),
        /** What happens after confirming, in the tenant's own words. */
        fulfilment_note: z.string().trim().min(1).optional(),
        extra_instructions: z.string().trim().min(1).optional(),
        /**
         * Whether the agent raises delivery on its own. `proactive` (the default,
         * a home-delivery shop): after confirming it asks for the address.
         * `on_request` (a trading company that ships but does not sell on it):
         * delivery, its cost and the address come up only when the customer asks.
         * This is behaviour, not wording, but it is an INDUSTRY trait and the
         * lines it switches are code-originated — the script has no other lever.
         */
        delivery_topic: z.enum(["proactive", "on_request"]).optional(),
    })
        .optional(),
    /**
     * The quote-request close: the team prices outside the system. The other close
     * shape next to `orders`; which one a tenant runs is its PLAN (`quotes` vs
     * `orders`), never the script — this block is only the words.
     */
    quotes: z
        .object({
        /** With article, singular: "la cotización", "la solicitud de cotización". */
        quote_noun: z.string().trim().min(1).optional(),
        /** What happens after submitting, in the tenant's own words. */
        fulfilment_note: z.string().trim().min(1).optional(),
        extra_instructions: z.string().trim().min(1).optional(),
    })
        .optional(),
    /**
     * Handing the prospect to a person. The MECHANICS are the same everywhere —
     * which tool, with what arguments, and that `user_requested` silences the
     * agent while `ai_initiated` does not — so the code keeps all of that. What it
     * cannot know is WHEN the next step needs a human, because that is the tenant's
     * own closing process: a school hands off to talk about inscripción, a shop to
     * close the order, a dealer for a formal quote.
     *
     * It used to guess, and the guess was the school's: every script rendered
     * "agendar visita, hablar de costo exacto, proceso de inscripción", so a shop
     * with no scheduling tools at all was being told to hand off in order to book
     * an appointment. Same shape as `documents.examples`, one layer up.
     */
    handoff: z
        .object({
        /** When `ai_initiated` applies, as a prose list: "agendar la visita al
         *  campus, hablar de costos exactos, el proceso de inscripción". Omitted →
         *  the code states the generic rule and gives no examples at all, which is
         *  bland but true for every industry. */
        examples: z.string().trim().min(1).optional(),
        extra_instructions: z.string().trim().min(1).optional(),
    })
        .optional(),
});
/**
 * Per-visit conversation sessions, declared by the INDUSTRY.
 *
 * Its presence is the third gate on the feature, next to the `CHAT_SESSIONS_ENABLED`
 * master switch and the tenant's `chat_sessions` plan feature — and it is the only
 * one of the three that is per-script. Plans and env vars are global, so a school
 * sharing a plan with a shop would be cut too; absence of this block is what keeps
 * every admissions tenant's prompt and history byte-identical.
 *
 * Both numbers are hours and both are optional: the env defaults
 * (`CHAT_SESSION_IDLE_HOURS`, `CHAT_SESSION_MAX_AGE_HOURS`) stand unless an
 * industry needs its own. A shop's visit goes cold in a couple of days; that is
 * not a claim we can make about every trade at once.
 */
const sessionPolicySchema = z.object({
    idle_hours: z.number().int().positive().optional(),
    max_age_hours: z.number().int().positive().optional(),
});
// ── ConversationScript (parsed JSON) ────────────────────────────────────────
const conversationScriptParsedBaseSchema = z.object({
    schema_version: z.literal(1),
    agent_identity: agentIdentitySchema,
    phases: z.array(phaseSchema).min(1),
    field_capture_rules: z.array(fieldCaptureRuleSchema).optional(),
    scoring: scoringSchema,
    handoff: handoffSchema,
    available_tools: z.array(toolNameSchema).min(1),
    vocabulary: vocabularySchema.optional(),
    capabilities: capabilityConfigSchema.optional(),
    session_policy: sessionPolicySchema.optional(),
});
export const conversationScriptParsedSchema = conversationScriptParsedBaseSchema.superRefine((data, ctx) => {
    // 1. Phase IDs must be unique
    const ids = data.phases.map((p) => p.id);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Phase IDs must be unique",
            path: ["phases"],
        });
    }
    // 2. Phase orders must be unique and contiguous 1..N
    const orders = [...data.phases.map((p) => p.order)].sort((a, b) => a - b);
    for (let i = 0; i < orders.length; i++) {
        if (orders[i] !== i + 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Phase orders must be unique and contiguous starting from 1",
                path: ["phases"],
            });
            break;
        }
    }
    // 3. At most one terminal phase (no exit_conditions), and it must be last
    const terminal = data.phases.filter((p) => !p.exit_conditions);
    if (terminal.length > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At most one terminal phase (without exit_conditions) is allowed",
            path: ["phases"],
        });
    }
    if (terminal.length === 1) {
        const maxOrder = Math.max(...data.phases.map((p) => p.order));
        if (terminal[0].order !== maxOrder) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "The terminal phase (without exit_conditions) must have the highest order",
                path: ["phases"],
            });
        }
    }
    // 4. Scoring ranges must not overlap
    const sortedRules = [...data.scoring.rules].sort((a, b) => a.range[0] - b.range[0]);
    for (let i = 1; i < sortedRules.length; i++) {
        const prev = sortedRules[i - 1];
        const curr = sortedRules[i];
        if (curr.range[0] <= prev.range[1]) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Scoring ranges overlap: [${prev.range.join(",")}] and [${curr.range.join(",")}]`,
                path: ["scoring", "rules"],
            });
        }
    }
});
export function parseConversationScript(raw) {
    return conversationScriptParsedSchema.parse(raw);
}
//# sourceMappingURL=conversation-script.js.map